import React, { useState, useEffect } from 'react';
import { Table, Spinner, Button, Form } from 'react-bootstrap';
import CustomModal from '../../Shared/CustomModal/CustomModal';
import userService from '../../../services/userService';
import showToast from '../../../utils/toast';
import { Images } from '../../../assets/assets';
import './MedicalUsers.css';

export default function MedicalUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [addUserForm, setAddUserForm] = useState({
    username: '',
    email: '',
    password: '',
    phoneNumber: '',
  });
  
  const [editUserForm, setEditUserForm] = useState({
    username: '',
    email: '',
    phoneNumber: '',
    isActive: true,
  });
  
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchMedicalUsers();
  }, []);

  const fetchMedicalUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsersWithLatestScan({ 
        role: 'medicalUser',
        limit: 1000 
      });
      
      if (response.data && response.data.length > 0) {
        const usersWithScans = response.data.map(user => ({
          id: user.id || user._id,
          _id: user.id || user._id,
          username: user.username,
          email: user.email,
          phoneNumber: user.phoneNumber,
          isActive: user.isActive,
          createdAt: user.createdAt,
          scanCount: user.scanCount || 0,
          lastLogin: user.createdAt
        }));
        
        setUsers(usersWithScans);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching medical users:', error);
      showToast.error(error.message || 'Failed to load medical users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserForm({
      username: user.username || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      isActive: user.isActive !== undefined ? user.isActive : true,
    });
    setShowEditModal(true);
  };

  const handleAddUser = () => {
    setAddUserForm({
      username: '',
      email: '',
      password: '',
      phoneNumber: '',
    });
    setShowAddModal(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setResetPasswordForm({
      newPassword: '',
      confirmPassword: '',
    });
    setShowResetPasswordModal(true);
  };

  const handleDeactivateUser = (user) => {
    setSelectedUser(user);
    setShowDeactivateModal(true);
  };

  const handleCreateUser = async () => {
    try {
      if (!addUserForm.username || !addUserForm.email || !addUserForm.password) {
        showToast.error('Please fill in all required fields');
        return;
      }

      if (addUserForm.password.length < 6) {
        showToast.error('Password must be at least 6 characters long');
        return;
      }

      setSaving(true);
      await userService.createUser({
        username: addUserForm.username,
        email: addUserForm.email,
        password: addUserForm.password,
        role: 'medicalUser',
        phoneNumber: addUserForm.phoneNumber || undefined,
      });

      showToast.success('User created successfully');
      setShowAddModal(false);
      setAddUserForm({ username: '', email: '', password: '', phoneNumber: '' });
      fetchMedicalUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      showToast.error(error.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      if (!editUserForm.username || !editUserForm.email) {
        showToast.error('Please fill in all required fields');
        return;
      }

      setSaving(true);
      await userService.updateUser(selectedUser.id, {
        username: editUserForm.username,
        email: editUserForm.email,
        phoneNumber: editUserForm.phoneNumber || undefined,
        isActive: editUserForm.isActive,
      });

      showToast.success('User updated successfully');
      setShowEditModal(false);
      fetchMedicalUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      showToast.error(error.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPasswordSubmit = async () => {
    try {
      if (!resetPasswordForm.newPassword) {
        showToast.error('Please enter a new password');
        return;
      }

      if (resetPasswordForm.newPassword.length < 6) {
        showToast.error('Password must be at least 6 characters long');
        return;
      }

      if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
        showToast.error('Passwords do not match');
        return;
      }

      setSaving(true);
      await userService.resetUserPassword(selectedUser.id, resetPasswordForm.newPassword);

      showToast.success('Password reset successfully');
      setShowResetPasswordModal(false);
      setResetPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error resetting password:', error);
      showToast.error(error.message || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateSubmit = async () => {
    try {
      setSaving(true);
      await userService.deactivateUser(selectedUser.id);

      showToast.success('User deactivated successfully');
      setShowDeactivateModal(false);
      fetchMedicalUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      showToast.error(error.message || 'Failed to deactivate user');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.username?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.id?.toString().includes(search)
    );
  });

  return (
    <div className="medical-users-container">
      <div className="custom-card">
        <div className="custom-card-header">
          <div className="custom-card-header-text">
            <h2>Medical Users Management</h2>
            <p>Manage medical users and their access to the platform</p>
          </div>
          <div className="custom-card-actions">
            <button className="btn btn-primary" onClick={handleAddUser}>
              <span className="icon">+</span>
              Add User
            </button>
          </div>
        </div>
        <div className="custom-card-body">
          {/* Search Bar */}
          <div className="search-section mb-3">
            <Form.Control
              type="text"
              placeholder="Search by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Users Table */}
          <div className="custom-table-wrapper">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading medical users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-5">
                <p>No medical users found</p>
                {searchTerm && (
                  <p className="text-muted">Try adjusting your search criteria</p>
                )}
              </div>
            ) : (
              <Table hover responsive className="custom-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Reports</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id || user._id}>
                      <td>
                        <div className="user-info">
                          <div className="user-name">{user.username || user.email}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </td>
                      <td>{getStatusBadge(user.isActive)}</td>
                      <td>{formatDate(user.lastLogin || user.createdAt)}</td>
                      <td>
                        <span className="reports-count">
                          {user.scanCount || 0}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div className="action-icons">
                          {/* <button
                            className="action-icon-btn"
                            onClick={() => handleEditUser(user)}
                            title="Edit User"
                          >
                            <img src={Images.EditIcon} alt="Edit" className="img-fluid" style={{ width: '18px', height: '18px' }} />
                          </button> */}
                          <button
                            className="action-icon-btn"
                            onClick={() => handleViewUser(user)}
                            title="View User"
                          >
                            <img src={Images.EyeIcon} alt="View" className="img-fluid" style={{ width: '18px', height: '18px' }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      <CustomModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        title="Add New User"
        footerButtons={[
          {
            label: 'Cancel',
            variant: 'secondary',
            onClick: () => setShowAddModal(false),
            disabled: saving
          },
          {
            label: 'Create User',
            variant: 'primary',
            onClick: handleCreateUser,
            disabled: saving,
            loading: saving
          }
        ]}
      >
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Username <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter username"
              value={addUserForm.username}
              onChange={(e) => setAddUserForm({ ...addUserForm, username: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              value={addUserForm.email}
              onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Password <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter password (min 6 characters)"
              value={addUserForm.password}
              onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
              required
              minLength={6}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Phone Number</Form.Label>
            <Form.Control
              type="tel"
              placeholder="Enter phone number (optional)"
              value={addUserForm.phoneNumber}
              onChange={(e) => setAddUserForm({ ...addUserForm, phoneNumber: e.target.value })}
            />
          </Form.Group>
        </Form>
      </CustomModal>

      {/* User Details Modal */}
      <CustomModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        title="User Details"
        size="lg"
        footerButtons={(() => {
          const baseButtons = [
            {
              label: 'Close',
              variant: 'secondary',
              onClick: () => setShowDetailsModal(false)
            },
            {
              label: 'Edit User',
              variant: 'primary',
              onClick: () => {
                setShowDetailsModal(false);
                handleEditUser(selectedUser);
              }
            },
            {
              label: 'Reset Password',
              variant: 'warning',
              onClick: () => {
                setShowDetailsModal(false);
                handleResetPassword(selectedUser);
              }
            }
          ];
          
          if (selectedUser?.isActive) {
            baseButtons.push({
              label: 'Deactivate User',
              variant: 'danger',
              onClick: () => {
                setShowDetailsModal(false);
                handleDeactivateUser(selectedUser);
              }
            });
          } else {
            baseButtons.push({
              label: 'Activate User',
              variant: 'success',
              onClick: async () => {
                try {
                  setSaving(true);
                  await userService.activateUser(selectedUser.id);
                  showToast.success('User activated successfully');
                  setShowDetailsModal(false);
                  fetchMedicalUsers();
                } catch (error) {
                  showToast.error(error.message || 'Failed to activate user');
                } finally {
                  setSaving(false);
                }
              },
              disabled: saving,
              loading: saving
            });
          }
          
          return baseButtons;
        })()}
      >
        {selectedUser && (
          <div className="user-details">
            <div className="detail-row">
              <span className="detail-label">Name:</span>
              <span className="detail-value">{selectedUser.username || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{selectedUser.email || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Phone Number:</span>
              <span className="detail-value">{selectedUser.phoneNumber || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className="detail-value">{getStatusBadge(selectedUser.isActive)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Login:</span>
              <span className="detail-value">{formatDate(selectedUser.lastLogin)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Reports Generated:</span>
              <span className="detail-value">{selectedUser.scanCount || 0}</span>
            </div>
          </div>
        )}
      </CustomModal>

      {/* Edit User Modal */}
      <CustomModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        title="Edit User"
        footerButtons={[
          {
            label: 'Cancel',
            variant: 'secondary',
            onClick: () => setShowEditModal(false),
            disabled: saving
          },
          {
            label: 'Save Changes',
            variant: 'primary',
            onClick: handleUpdateUser,
            disabled: saving,
            loading: saving
          }
        ]}
      >
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Username <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter username"
              value={editUserForm.username}
              onChange={(e) => setEditUserForm({ ...editUserForm, username: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              value={editUserForm.email}
              onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Phone Number</Form.Label>
            <Form.Control
              type="tel"
              placeholder="Enter phone number (optional)"
              value={editUserForm.phoneNumber}
              onChange={(e) => setEditUserForm({ ...editUserForm, phoneNumber: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={editUserForm.isActive}
              onChange={(e) => setEditUserForm({ ...editUserForm, isActive: e.target.checked })}
            />
          </Form.Group>
        </Form>
      </CustomModal>

      {/* Reset Password Modal */}
      <CustomModal
        show={showResetPasswordModal}
        onHide={() => setShowResetPasswordModal(false)}
        title="Reset Password"
        footerButtons={[
          {
            label: 'Cancel',
            variant: 'secondary',
            onClick: () => setShowResetPasswordModal(false),
            disabled: saving
          },
          {
            label: 'Reset Password',
            variant: 'warning',
            onClick: handleResetPasswordSubmit,
            disabled: saving,
            loading: saving
          }
        ]}
      >
        <p>Reset password for <strong>{selectedUser?.username || selectedUser?.email}</strong></p>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>New Password <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter new password (min 6 characters)"
              value={resetPasswordForm.newPassword}
              onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
              required
              minLength={6}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="password"
              placeholder="Confirm new password"
              value={resetPasswordForm.confirmPassword}
              onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
              required
              minLength={6}
            />
          </Form.Group>
        </Form>
      </CustomModal>

      {/* Deactivate User Modal */}
      <CustomModal
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
        title="Deactivate User"
        footerButtons={[
          {
            label: 'Cancel',
            variant: 'secondary',
            onClick: () => setShowDeactivateModal(false),
            disabled: saving
          },
          {
            label: 'Deactivate User',
            variant: 'danger',
            onClick: handleDeactivateSubmit,
            disabled: saving,
            loading: saving
          }
        ]}
      >
        <p>Are you sure you want to deactivate user <strong>{selectedUser?.username || selectedUser?.email}</strong>?</p>
        <p className="text-warning">This will revoke all active sessions and prevent the user from logging in.</p>
      </CustomModal>
    </div>
  );
}
